import React, { useState, useEffect, useMemo } from "react";
import { format, parseISO, differenceInDays } from "date-fns"; // Added differenceInDays import
import {
  EditButton,
  List,
  ShowButton,
  useDataGrid,
  DeleteButton,
  CreateButton,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  BaseRecord,
  getDefaultFilter,
  HttpError,
  IResourceComponentsProps,
  useTranslate,
  useList,
} from "@refinedev/core";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Snackbar,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import axiosInstance from "api/axios-config";
import axios from "axios";
import { useAuth } from "../../contexts/auth-context";

export interface IEvent {
  id: number;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  latitude: number;
  longitude: number;
  geofenceRadius: number;
  isActive: boolean;
  createdById: object;
  securityContacts: any[];
  city?: string; // Add city field
}

export interface IEventFilterVariables {
  q?: string;
  name?: string;
  isActive?: boolean;
}

// Function to get the city from coordinates using Geoapify
const getCityFromCoordinates = async (
  latitude: number,
  longitude: number,
  translate: any
): Promise<string> => {
  try {
    if (!latitude || !longitude) {
      return translate("events.unknownLocation", "Unknown location");
    }

        const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;

    if (!apiKey) {
      console.error("REACT_APP_GEOAPIFY_API_KEY is not set in environment variables");
      return translate("events.errorLocation", "API key not configured");
    }

    const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&format=json&apiKey=${apiKey}`;

    const response = await axios.get(url);

    if (response.data && response.data.results && response.data.results.length > 0) {
      const result = response.data.results[0];
      // Check for city, falling back to county or state if city isn't available
      const city = result.city || result.county || result.state || translate("events.unknownLocation", "Unknown location");
      return city;
    }

    if (response.data && response.data.error_message) {
      console.error("Geocoding API returned error:", response.data);
      return translate("events.errorLocation", "Error getting location: " + response.data.error_message);
    }

    return translate("events.unknownLocation", "Unknown location");
    } catch (error: any) {
    console.error("Error during reverse geocoding:", error);
    return translate("events.errorLocation", "Error getting location");
  }
};

export const EventList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user } = useAuth();

  // Add state for clipboard notification
  const [copySnackbar, setCopySnackbar] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setCopySnackbar({
      ...copySnackbar,
      open: false,
    });
  };

  // État pour la pagination locale - MOVED UP to fix declaration order
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Ajout d'un état de chargement pour le filtrage
  const [isFiltering, setIsFiltering] = useState(false);
  // État pour stocker tous les éléments et éléments filtrés
  const [filteredItems, setFilteredItems] = useState<IEvent[]>([]);
  // État pour stocker les anciennes lignes (pour éviter le flash de toutes les lignes)
  const [lastValidRows, setLastValidRows] = useState<IEvent[]>([]);
  // État pour les critères de filtrage actifs
  const [activeFilters, setActiveFilters] = useState<IEventFilterVariables>({});
  // State for storing events with city info
  const [eventsWithCity, setEventsWithCity] = useState<IEvent[]>([]);
  // State for tracking loading of city data
  const [loadingCities, setLoadingCities] = useState(false);

  // Simplify data fetching to match PromocodeList approach
  const { data: eventsData } = useList<IEvent>({
    resource: "safe-events",
    // Add filter for SAFEE_MODERATOR to only see events where they are security contacts
    ...(user?.role === "SAFEE_MODERATOR" && {
      filters: [
        {
          field: "securityContactUserId",
          operator: "eq",
          value: user.id,
        },
      ],
    }),
  });

  // For debugging purposes, we'll keep these loading and error states
  const eventsLoading = !eventsData;
  const eventsError = null;
  const allItems = useMemo(() => eventsData?.data || [], [eventsData]);

  // Fetch city data for all events
  useEffect(() => {
    const fetchCitiesForEvents = async () => {
      if (!allItems.length) return;

      setLoadingCities(true);

      try {
        // Create a copy of allItems to avoid modifying the original data
        const itemsWithCity = [...allItems];

        // Process items in batches to avoid overwhelming the API
        const batchSize = 5;
        for (let i = 0; i < itemsWithCity.length; i += batchSize) {
          const batch = itemsWithCity.slice(i, i + batchSize);

          // Fetch cities in parallel for this batch
          await Promise.all(
            batch.map(async (event, index) => {
              const cityName = await getCityFromCoordinates(event.latitude, event.longitude, translate);
              itemsWithCity[i + index] = { ...event, city: cityName };
            })
          );
        }

        setEventsWithCity(itemsWithCity);
      } catch (error) {
        console.error("Error fetching cities for events:", error);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCitiesForEvents();
  }, [allItems, translate]);

  const { dataGridProps, search, filters } = useDataGrid<
    IEvent,
    HttpError,
    IEventFilterVariables
  >({
    initialPageSize: 10,
    onSearch: (params) => {
      // Activer l'état de filtrage
      setIsFiltering(true);
      // Stockez les filtres actuels
      setActiveFilters(params);

      // Sauvegarder l'état actuel des lignes
      if (dataGridProps.rows && dataGridProps.rows.length > 0) {
        setLastValidRows(dataGridProps.rows as IEvent[]);
      }

      // Filtrage côté client, retourner un tableau vide pour les filtres API
      return [];
    },
    pagination: { mode: "client" },
  });

  // Effet pour filtrer les éléments selon les critères
  useEffect(() => {
    if (!eventsWithCity.length) return;

    // Fonction pour filtrer les éléments selon les critères actifs
    const performFiltering = async () => {
      let result = [...eventsWithCity];
      const { name, isActive } = activeFilters;

      // Filtre par name s'il est spécifié
      if (name && name !== "") {
        result = result.filter((item) =>
          item.name.toLowerCase().includes(name.toLowerCase())
        );
      }

      // Filtre par statut actif s'il est spécifié
      if (isActive !== undefined && isActive !== null) {
        result = result.filter((item) => item.isActive === Boolean(isActive));
      }

      // Utiliser setTimeout pour éviter le flash des résultats
      setTimeout(() => {
        setFilteredItems(result);
        setIsFiltering(false);
      }, 50);
    };

    performFiltering();
  }, [activeFilters, eventsWithCity]);

  // Effet pour mettre à jour lastValidRows séparément
  useEffect(() => {
    if (!isFiltering && dataGridProps.rows && dataGridProps.rows.length > 0) {
      setLastValidRows(dataGridProps.rows as IEvent[]);
    }
  }, [dataGridProps.rows, isFiltering]);

  // Override des lignes du DataGrid avec nos données filtrées mais en préservant la pagination
  const getFilteredRows = () => {
    // Pendant le filtrage, afficher les dernières lignes valides pour éviter un flash
    if (isFiltering && lastValidRows.length > 0) {
      return lastValidRows;
    }

    // If we're loading city data, use the original data
    if (loadingCities) {
      return dataGridProps.rows || [];
    }

    // Si aucun filtre actif, utiliser les données originales du dataGridProps pour préserver pagination
    if (
      (!activeFilters.name || activeFilters.name === "") &&
      (activeFilters.isActive === undefined || activeFilters.isActive === null)
    ) {
      // Use eventsWithCity instead of the original rows to include city information
      return eventsWithCity;
    }

    // Si filtrage terminé, appliquer la pagination manuellement aux résultats
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return filteredItems.slice(start, end);
  };

  // Debug filteredRows being used by the DataGrid
  const rowsForGrid = getFilteredRows();
  useEffect(() => {}, [rowsForGrid]);

  const columns = React.useMemo<GridColDef<IEvent>[]>(
    () => [
      {
        field: "isActive",
        headerName: "",
        width: 40,
        minWidth: 40,
        flex: 0,
        filterable: false,
        sortable: false,
        renderCell: function render({ value }) {
          return (
            <Tooltip
              title={
                value
                  ? translate("isActive.active", "Active")
                  : translate("isActive.inactive", "Inactive")
              }
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  cursor: "help",
                }}
              >
                {value ? "🟢" : "🔴"}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "name",
        flex: 1,
        headerName: translate("events.fields.name", "Event Name"),
        minWidth: 180,
        renderCell: function render({ row }) {
          try {
            // Calculate days remaining if start date exists
            let daysRemainingElement = null;

            if (row.startDate) {
              // Handle both Date objects and strings
              const dateValue =
                typeof row.startDate === "string" ? parseISO(row.startDate) : row.startDate;

              // Check if the start date is in the future
              const today = new Date();
              const daysRemaining = differenceInDays(dateValue, today);

              if (daysRemaining > 0) {
                daysRemainingElement = (
                  <span
                    style={{
                      backgroundColor: "#004366",
                      color: "white",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      marginLeft: "8px",
                      fontWeight: "normal",
                    }}
                  >
                    {translate("events.fields.inDays")} {daysRemaining} {translate("events.fields.days")}
                  </span>
                );
              }
            }

            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold' }}>{row.name}</span>
                {daysRemainingElement}
              </Box>
            );
          } catch (error) {
            return row.name;
          }
        },
      },
      {
        field: "city",
        flex: 1,
        headerName: translate("events.fields.city", "City"),
        minWidth: 150,
        renderCell: function render({ row }) {
          if (loadingCities && !row.city) {
            return <CircularProgress size={20} />;
          }
          return (
            <Tooltip title={row.city || translate("events.unknownLocation", "Unknown location")}>
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {row.city || translate("events.unknownLocation", "Unknown location")}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "startDate",
        flex: 1,
        headerName: translate("events.fields.startDate", "Start Date"),
        minWidth: 120,
        renderCell: function render({ value }) {
          try {
            if (!value) return "";

            // Handle both Date objects and strings
            const dateValue =
              typeof value === "string" ? parseISO(value) : value;

            // Return the date in standard format without days remaining
            return format(dateValue, "dd/MM/yyyy HH:mm");
          } catch (error) {
            return "";
          }
        },
      },
      {
        field: "endDate",
        flex: 1,
        headerName: translate("events.fields.endDate", "End Date"),
        minWidth: 120,
        renderCell: function render({ value }) {
          try {
            if (!value) return "";

            // Handle both Date objects and strings
            const dateValue =
              typeof value === "string" ? parseISO(value) : value;
            return format(dateValue, "dd/MM/yyyy HH:mm");
          } catch (error) {
            return "";
          }
        },
      },
      {
        field: "geofenceRadius",
        flex: 0,
        headerName: translate("events.fields.radius", "Radius (m)"),
        type: "number",
        width: 100,
        minWidth: 100,
        renderCell: function render({ value }) {
          return `${value}m`;
        },
      },
      {
        field: "actions",
        headerName: translate("events.fields.actions", "Actions"),
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteButton hideText recordItemId={row.id} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 120,
      },
    ],
    [translate, loadingCities] // Added loadingCities as a dependency
  );

  const { register, handleSubmit, control, reset } = useForm<
    BaseRecord,
    HttpError,
    IEventFilterVariables
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq") || "",
      name: getDefaultFilter("name", filters, "contains") || "",
      isActive: getDefaultFilter("isActive", filters, "eq"),
    },
  });

  // Add reset function to clear filters
  const handleClearFilters = () => {
    reset({
      q: "",
      name: "",
      isActive: undefined,
    });
    search({ q: "", name: "", isActive: undefined });
  };

  if (eventsLoading) {
    return <div>Loading events...</div>;
  }

  if (eventsError) {
    return <div>Error loading events. Check the console for details.</div>;
  }

  if (!eventsData?.data || eventsData.data.length === 0) {
    return (
      <div>
        No events found. {eventsData ? "Data is empty." : "Data is undefined."}
      </div>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Card>
            <CardHeader title={translate("pages.filter", "Filters")} />
            <CardContent sx={{ pt: 0 }}>
              <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column" }}
                autoComplete="off"
                onSubmit={handleSubmit(search)}
              >
                <TextField
                  {...register("name")}
                  label={translate("pages.search", "Search")}
                  placeholder={translate(
                    "events.searchByName",
                    "Search by event name"
                  )}
                  margin="normal"
                  fullWidth
                  autoFocus
                  size="small"
                />
                <Controller
                  control={control}
                  name="isActive"
                  render={({ field }) => (
                    <Autocomplete
                      options={[true, false, null]}
                      {...field}
                      onChange={(_, value) => {
                        field.onChange(value);
                      }}
                      getOptionLabel={(item) => {
                        if (item === true) return "Active";
                        if (item === false) return "Inactive";
                        return "All";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("isActive.title", "Active Status")}
                          placeholder={translate(
                            "isActive.select",
                            "Select status"
                          )}
                          margin="normal"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button type="submit" variant="contained">
                    {translate("buttons.search", "Search")}
                  </Button>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    {translate("buttons.clear", "Clear")}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={9}>
          <List
            headerButtons={({ createButtonProps }) => {
              return (
                <>
                  {user?.role !== "SAFEE_MODERATOR" && (
                    <CreateButton {...createButtonProps} />
                  )}
                </>
              );
            }}
          >
            <DataGrid
              {...dataGridProps}
              rows={rowsForGrid}
              rowCount={
                // Si filtrage actif, utiliser le nombre total d'éléments filtrés
                (!activeFilters.name || activeFilters.name === "") &&
                (activeFilters.isActive === undefined ||
                  activeFilters.isActive === null)
                  ? dataGridProps.rowCount
                  : filteredItems.length
              }
              columns={columns}
              autoHeight
              loading={eventsLoading || isFiltering || loadingCities} // Added loadingCities to loading indicator
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                setPaginationModel(model);
              }}
              pageSizeOptions={[10]}
              paginationMode="server"
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
            />
          </List>
        </Grid>
      </Grid>
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
